import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from '../lib/supabase';

const cx = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

// Helper to split option text on " — " for display purposes
const splitOptionText = (option: string): { main: string; sub?: string } => {
  const separators = [' — ', ' - '];
  for (const sep of separators) {
    if (option.includes(sep)) {
      const [main, ...rest] = option.split(sep);
      return { main, sub: rest.join(sep) };
    }
  }
  return { main: option };
};

function ChevronIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <svg
      className={cx(
        'h-5 w-5 text-[#304031] transition-transform duration-300 ease-out',
        isOpen && 'rotate-180'
      )}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 7.5L10 12.5L15 7.5" />
    </svg>
  );
}

type FocusAccordionItemProps = {
  category: string;
  selectedCount: number;
  totalCount: number;
  isOpen: boolean;
  onToggleOpen: () => void;
  children: React.ReactNode;
};

function FocusAccordionItem({
  category,
  selectedCount,
  totalCount,
  isOpen,
  onToggleOpen,
  children
}: FocusAccordionItemProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(isOpen ? undefined : 0);
  const hasSelections = selectedCount > 0 || isOpen;

  useEffect(() => {
    if (!contentRef.current) return;

    if (isOpen) {
      const contentHeight = contentRef.current.scrollHeight;
      setHeight(contentHeight);
      const timeout = setTimeout(() => setHeight(undefined), 300);
      return () => clearTimeout(timeout);
    } else {
      const contentHeight = contentRef.current.scrollHeight;
      setHeight(contentHeight);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setHeight(0);
        });
      });
    }
  }, [isOpen]);

  return (
    <div
      className={cx(
        'overflow-hidden rounded-2xl border-2 transition-all duration-200',
        hasSelections ? 'border-[#304031] bg-[#304031]/5' : 'border-[#304031]/20',
        !hasSelections && 'hover:border-[#304031]/40',
        isOpen ? 'bg-[#FFFCEF]' : 'bg-[#FFFCEF]/60 hover:bg-[#FFFCEF]'
      )}
    >
      <button
        type="button"
        onClick={onToggleOpen}
        aria-expanded={isOpen}
        aria-controls={`accordion-content-${category}`}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors"
      >
        <div className="flex items-center gap-4">
          <span
            aria-hidden
            className={cx(
              'flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 transition',
              hasSelections ? 'border-[#304031] bg-[#304031]' : 'border-[#304031]/40'
            )}
          >
            {hasSelections && (
              <svg
                className="h-4 w-4 text-white"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="2,6 5,9 10,3" />
              </svg>
            )}
          </span>
          <div className="flex flex-col gap-0.5">
            <span className="text-base font-semibold text-[#304031]">{category}</span>
            {!isOpen && selectedCount > 0 && (
              <span className="text-sm text-[#304031]/70">
                {selectedCount} of {totalCount} selected
              </span>
            )}
            {!isOpen && selectedCount === 0 && (
              <span className="text-sm text-[#304031]/50">
                {totalCount} options available
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {selectedCount > 0 && isOpen && (
            <span className="flex h-6 min-w-[24px] items-center justify-center rounded-full bg-[#304031] px-2 text-xs font-semibold text-[#fef6d6]">
              {selectedCount}
            </span>
          )}
          <ChevronIcon isOpen={isOpen} />
        </div>
      </button>
      <div
        id={`accordion-content-${category}`}
        ref={contentRef}
        style={{ height: height !== undefined ? `${height}px` : 'auto' }}
        className="overflow-hidden transition-[height] duration-300 ease-out"
      >
        <div className="px-5 pb-5">
          {children}
        </div>
      </div>
    </div>
  );
}

type StepType = 'radio' | 'textarea' | 'checkbox';

interface RadioStep {
  id: string;
  question: string;
  type: 'radio';
  options: string[];
  value: string;
  setValue: (value: string) => void;
  required: boolean;
}

interface TextareaStep {
  id: string;
  question: string;
  type: 'textarea';
  value: string;
  setValue: (value: string) => void;
  placeholder?: string;
  required: boolean;
}

interface CheckboxStep {
  id: string;
  question: string;
  type: 'checkbox';
  options: string[];
  value: string[];
  setValue: (value: string[]) => void;
  required: boolean;
}

type Step = RadioStep | TextareaStep | CheckboxStep;

export default function ExperienceForm({ onComplete }: { onComplete?: () => void }) {
  const supabase = getSupabaseClient();

  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [openAccordions, setOpenAccordions] = useState<Set<string>>(new Set());
  const [excludedOpenAccordions, setExcludedOpenAccordions] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [experienceTypes, setExperienceTypes] = useState<string[]>([]);
  const [legalStatus, setLegalStatus] = useState('');
  const [supportTypes, setSupportTypes] = useState<string[]>([]);
  const [travelWillingness, setTravelWillingness] = useState<string[]>([]);
  const [serviceLocations, setServiceLocations] = useState<string[]>([]);
  const [focusAreas, setFocusAreas] = useState<string[]>([]);
  const [excludedAreas, setExcludedAreas] = useState<string[]>([]);
  const [worksWithMedications, setWorksWithMedications] = useState('');
  const [medicationQualifications, setMedicationQualifications] = useState('');
  const [specialGroups, setSpecialGroups] = useState<string[]>([]);
  const [religiousBeliefs, setReligiousBeliefs] = useState<string[]>([]);
  const [focusSelectionError, setFocusSelectionError] = useState<string | null>(null);

  const focusAreaOptions = {
    "Personal Growth & Exploration": [
      "Personal Development", "Empowerment", "Creative Expression",
      "Spiritual Exploration", "Perspective Shifting"
    ],
    "Mental & Emotional Wellbeing": [
      "Anxiety", "Depression", "Mood Disorders", "ADD / ADHD", "OCD",
      "Autism", "Bipolar", "Eating Disorders", "General Mental Health Support"
    ],
    "Trauma & Recovery": [
      "PTSD", "Early Childhood Trauma", "Sexual Trauma", "Relationship Trauma",
      "Religious Trauma", "Racial Trauma", "Physical Trauma",
      "Cult / High-Control Group Recovery", "Shame", "Inner Child Work"
    ],
    "Addiction & Behavioral Patterns": [
      "Addiction / Substance Use", "Anger Management", "Pain Management"
    ],
    "Relationships & Family": [
      "Couples Support", "Divorce & Separation", "Healthy Relationship Skills",
      "Interpersonal Relationships", "Parenting / Children"
    ],
    "Life Transitions & Identity": [
      "Birth & Postpartum Support", "Career Support", "Life Transitions",
      "Grief & Loss", "Gender Identity", "End-of-Life Support"
    ],
    "Other": [
      "Other"
    ]
  };

  const focusAreaCategories = Object.keys(focusAreaOptions);
  const allFocusAreaOptions = Object.values(focusAreaOptions).flat();
  const allowedFocusAreas = new Set(allFocusAreaOptions);

  // Scroll to top when navigating between questions
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [currentStep]);

  // Load existing data on component mount
  useEffect(() => {
    const loadExistingData = async () => {
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) {
          console.error('Auth error:', authError);
          setLoading(false);
          return;
        }

        // Try to load existing professional experience data
        const { data: existingData, error } = await supabase
          .from('guide_professional_experience')
          .select('*')
          .eq('guide_id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
          console.error('Error loading experience data:', error);
        } else if (existingData) {
          // Populate form with existing data
          setExperienceTypes(existingData.experience_types || []);
          setLegalStatus(existingData.legal_status || '');
          setSupportTypes(existingData.support_types || []);
          setTravelWillingness(existingData.travel_willingness || []);
          setServiceLocations(existingData.service_locations || []);
          const rawFocusAreas = Array.isArray(existingData.focus_areas) ? existingData.focus_areas : [];
          const rawExcludedAreas = Array.isArray(existingData.excluded_areas) ? existingData.excluded_areas : [];
          const sanitizedExcludedAreas = rawExcludedAreas.filter((area: string) => allowedFocusAreas.has(area));
          setExcludedAreas(sanitizedExcludedAreas);
          const filteredFocusAreas = rawFocusAreas
            .filter((area: string) => allowedFocusAreas.has(area))
            .filter((area: string) => !sanitizedExcludedAreas.includes(area));
          setFocusAreas(filteredFocusAreas);
          // Open accordions for categories that have selected focus areas
          const categoriesToOpen = focusAreaCategories.filter((category) => {
            const options = focusAreaOptions[category as keyof typeof focusAreaOptions];
            return filteredFocusAreas.some((area: string) => options.includes(area));
          });
          setOpenAccordions(new Set(categoriesToOpen));
          // Open accordions for categories that have excluded areas
          if (sanitizedExcludedAreas.length > 0) {
            const excludedCategoriesToOpen = focusAreaCategories.filter((category) => {
              const options = focusAreaOptions[category as keyof typeof focusAreaOptions];
              return sanitizedExcludedAreas.some((area: string) => options.includes(area));
            });
            setExcludedOpenAccordions(new Set(excludedCategoriesToOpen));
          }
          setWorksWithMedications(existingData.works_with_medications || '');
          setMedicationQualifications(existingData.medication_qualifications || '');
          setSpecialGroups(existingData.special_groups || []);
          setReligiousBeliefs(existingData.religious_beliefs || []);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadExistingData();
  }, []);

  const steps: Step[] = [
    {
      id: 'experienceTypes',
      question: 'What type of experiences do you currently facilitate?',
      type: 'checkbox',
      options: [
        "1:1 Journeys or Sessions — Private, personalized experiences with dedicated support",
        "Small Group Experiences — Shared healing spaces with multiple participants",
        "Retreat Center Facilitation — Multi-day immersions with structured guidance",
        "Online Journey Support / Sitting — Virtual presence and harm-reduction support",
        "Microdosing Coaching or Support — Gentle, sub-perceptual dosing with guidance",
        "Medication Tapering Support — In collaboration with licensed providers",
        "Preparation & Integration Only — No in-person dosing, focused on readiness and processing",
        "Family / Couples Containers — Facilitated journeys for relational healing",
        "Other"
      ],
      value: experienceTypes,
      setValue: setExperienceTypes,
      required: false
    },
    {
      id: 'legalStatus',
      question: 'Which of the following best describes the context you work in?',
      type: 'radio',
      options: [
        "Government regulated options only — I work only in legal psychedelic modalities where I live",
        "Underground practitioner — I work in contexts that may not be formally legal where I live",
        "Both — I work in legal contexts and in underground settings",
        "Prefer not to say"
      ],
      value: legalStatus,
      setValue: setLegalStatus,
      required: true
    },
    {
      id: 'supportTypes',
      question: 'What type(s) of support do you offer?',
      type: 'checkbox',
      options: [
        "Microdose Support — Gentle, sub-perceptual dosing with guidance, structure, and harm reduction",
        "Macrodose Support — Full, immersive psychedelic experiences with trained facilitation",
        "Preparation Support — Help getting ready for a journey — intentions, mindset, safety planning",
        "Integration Support — Support after an experience to process insights and apply them",
        "Other"
      ],
      value: supportTypes,
      setValue: setSupportTypes,
      required: false
    },
    {
      id: 'travelWillingness',
      question: 'Are you willing to travel to work with clients?',
      subtext: 'This helps us understand how you prefer to offer your services and what geographic range you\'re comfortable supporting.',
      type: 'checkbox',
      options: [
        "Local / Regional — I'm available for seekers in my local area or region",
        "Within State/Province — I can travel within my state or province",
        "Nationally — I can travel to other states or provinces",
        "Internationally — I can travel abroad for the right clients",
        "Seekers Travel to Me — People typically travel to my location",
        "Virtual / Online — I work remotely via video"
      ],
      value: travelWillingness,
      setValue: setTravelWillingness,
      required: true
    },
    {
      id: 'serviceLocations',
      question: 'Where do you typically provide your services?',
      subtext: 'This helps us understand the type of environment you offer for preparation, facilitation, and integration.',
      type: 'checkbox',
      options: [
        "At My Home — Sessions at my personal residence",
        "Office or Professional Space — In a dedicated professional setting",
        "At the Client's Home — In-home sessions at the seeker's location",
        "Online / Virtual — Remote support via video",
        "At Retreat Centers — At dedicated retreat facilities",
        "Church, Non-Profit, or Spiritual Community — Community-based settings",
        "Other"
      ],
      value: serviceLocations,
      setValue: setServiceLocations,
      required: false
    },
    {
      id: 'focusAreas',
      question: 'What would you like to focus on with seekers?',
      type: 'checkbox',
      options: allFocusAreaOptions,
      value: focusAreas,
      setValue: setFocusAreas,
      required: false
    },
    {
      id: 'excludedAreas',
      question: 'Are there any conditions you specifically DO NOT work with?',
      type: 'checkbox',
      options: allFocusAreaOptions,
      value: excludedAreas,
      setValue: setExcludedAreas,
      required: false
    },
    {
      id: 'worksWithMedications',
      question: 'Do you work with clients who are currently taking psychiatric medications?',
      subtext: 'Some seekers may be on medications that can impact preparation, dosage, or safety considerations. Your answer helps us match you only with clients who fit safely within your scope and comfort level.',
      type: 'radio',
      options: ["Yes", "No"],
      value: worksWithMedications,
      setValue: setWorksWithMedications,
      required: false
    },
    {
      id: 'medicationQualifications',
      question: 'Please describe your qualifications and safety protocols for working with clients on psychiatric medications.',
      type: 'textarea',
      value: medicationQualifications,
      setValue: setMedicationQualifications,
      placeholder: 'Describe any formal training, clinical background, use of medical professionals for screening, safety protocols, or experience with medication-interaction harm reduction...',
      required: false
    },
    {
      id: 'specialGroups',
      question: 'Do you specialize in supporting any particular communities?',
      type: 'checkbox',
      options: [
        "BIPOC", "LGBTQ+", "Women", "Men", "Couples", "Families",
        "Veterans", "Seniors", "Complex PTSD", "Other", "None"
      ],
      value: specialGroups,
      setValue: setSpecialGroups,
      required: false
    },
    {
      id: 'religiousBeliefs',
      question: 'Do you identify with any particular spiritual or religious traditions?',
      subtext: 'This information is optional and helps us match seekers with guides whose worldview aligns with their comfort level.\n\nYou\'re welcome to select one, many, or none.',
      type: 'checkbox',
      options: [
        "Agnostic", "Atheist", "New Age / Spiritual",
        "Christian (Non-Denominational)", "Catholic", "Mormon / Latter-day Saint",
        "Jewish", "Muslim", "Buddhist", "Hindu",
        "Other", "None"
      ],
      value: religiousBeliefs,
      setValue: setReligiousBeliefs,
      required: false
    }
  ];

  const currentStepData = steps[currentStep];

  // Helper to check if a step should be skipped
  const shouldSkipStep = (stepId: string): boolean => {
    return stepId === 'medicationQualifications' && worksWithMedications !== 'Yes';
  };

  // Calculate if this is the last step (accounting for conditionally skipped steps)
  const getEffectiveNextStep = (fromStep: number): number => {
    let nextStep = fromStep + 1;
    while (nextStep < steps.length && shouldSkipStep(steps[nextStep].id)) {
      nextStep++;
    }
    return nextStep;
  };

  const getEffectivePrevStep = (fromStep: number): number => {
    let prevStep = fromStep - 1;
    while (prevStep >= 0 && shouldSkipStep(steps[prevStep].id)) {
      prevStep--;
    }
    return prevStep;
  };

  const isLastStep = getEffectiveNextStep(currentStep) >= steps.length;

  // Auto-advance for radio buttons and single-answer questions - DISABLED
  // useEffect(() => {
  //   if (currentStepData?.type === 'radio' && currentStepData.value && !saving) {
  //     const timer = setTimeout(() => {
  //       if (currentStep < steps.length - 1) {
  //         setCurrentStep(currentStep + 1);
  //       }
  //     }, 800); // Small delay for better UX
  //     return () => clearTimeout(timer);
  //   }
  // }, [currentStepData?.value, currentStep, steps.length, saving]);

  function toggleArrayValue<T>(arr: T[], value: T, setter: (v: T[]) => void) {
    if (arr.includes(value)) {
      setter(arr.filter((v) => v !== value));
    } else {
      setter([...arr, value]);
    }
  }

  const toggleAccordion = (category: string) => {
    setOpenAccordions((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const getCategoryFocusAreas = (category: string | undefined) => {
    if (!category) return [];
    const options = focusAreaOptions[category as keyof typeof focusAreaOptions];
    return focusAreas.filter((area) => options.includes(area));
  };

  const handleFocusAreaToggle = (area: string) => {
    setFocusAreas((prev) => {
      const isSelected = prev.includes(area);
      if (isSelected) {
        const updated = prev.filter((value) => value !== area);
        setFocusSelectionError(null);
        return updated;
      }
      if (prev.length >= 10) {
        setFocusSelectionError('You can select up to 10 focus areas total. Remove one to add another.');
        return prev;
      }
      setFocusSelectionError(null);
      return [...prev, area];
    });
  };

  
  const canProceedToNext = () => {
    if (!currentStepData.required) return true;
    
    if (currentStepData.type === 'radio') {
      return !!currentStepData.value;
    } else if (currentStepData.type === 'checkbox') {
      return currentStepData.value.length > 0;
    } else if (currentStepData.type === 'textarea') {
      return !!currentStepData.value.trim();
    }
    return true;
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      const prevStep = getEffectivePrevStep(currentStep);
      if (prevStep >= 0) {
        setCurrentStep(prevStep);
      } else {
        navigate('/onboarding');
      }
    } else {
      // Go back to the previous experience form at its last step
      navigate('/onboarding', { state: { returnToOldExperience: true } });
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const { data: { session }, error: authError } = await supabase.auth.getSession();
      if (authError || !session) {
        alert('Authentication error. Please log in again.');
        return;
      }

      // Prepare the request body
      const requestBody = {
        experience_types: experienceTypes,
        legal_status: legalStatus,
        support_types: supportTypes,
        travel_willingness: travelWillingness,
        service_locations: serviceLocations,
        focus_categories: focusAreaCategories.filter((category) => {
          const options = focusAreaOptions[category as keyof typeof focusAreaOptions];
          return focusAreas.some((area) => options.includes(area));
        }),
        focus_areas: focusAreas,
        excluded_areas: excludedAreas,
        works_with_medications: worksWithMedications || undefined,
        medication_qualifications: worksWithMedications === 'Yes' ? (medicationQualifications || undefined) : undefined,
        special_groups: specialGroups,
        religious_beliefs: religiousBeliefs,
      };

      const { data, error } = await supabase.functions.invoke('guide-professional-experience', {
        body: requestBody,
      });

      if (error) {
        console.error('Error saving experience data:', error);
        throw new Error(error.message || 'Failed to invoke edge function');
      }

      if (!data?.success) {
        const errorMessage = data?.error || 'Failed to save professional experience data';
        console.error('Edge function returned error:', data);
        throw new Error(errorMessage);
      }

      // Trigger the refresh function instead of navigating
      // Don't set saving to false here - let navigation handle it
      if (onComplete) {
        onComplete();
      } else {
        navigate('/onboarding');
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      alert(error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.');
      setSaving(false); // Only reset on error
    }
  };

  const handleNext = async () => {
    const nextStep = getEffectiveNextStep(currentStep);
    if (nextStep < steps.length) {
      setCurrentStep(nextStep);
    } else {
      await handleSubmit();
    }
  };

  const renderQuestion = () => {
    const step = currentStepData;

    // Special handling for focus areas question - now uses accordion UI
    if (step.id === 'focusAreas') {
      return (
        <div>
          <div className="mb-8">
            <h2 className="text-[42px] leading-[44px] font-medium mb-3 text-[#304031] font-recoleta">What would you like this work to focus on?</h2>
            <div className="space-y-3">
              <p className="text-[#304031]/70">
                Select the specific areas that align with your experience, training, and capacity. This helps us match you with seekers who need support in these areas.
              </p>
            </div>
          </div>

          {/* Summary bar */}
          <div className="mb-6 rounded-2xl bg-[#214d3c] p-6">
            <p className="text-xl font-bold text-[#fffcef] leading-[1.6] mb-2">
              Focus areas: {focusAreas.length} selected
            </p>
            {focusAreas.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {focusAreas.map((area) => (
                  <span
                    key={area}
                    className="inline-flex items-center h-8 rounded-full border border-[#8d8d8d] px-3 text-sm text-[#fef6d6]"
                  >
                    {area}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#fef6d6]/70">No focus areas selected yet</p>
            )}
          </div>

          {focusSelectionError && (
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {focusSelectionError}
            </div>
          )}

          {/* Accordion list */}
          <div className="flex flex-col gap-3">
            {focusAreaCategories.map((category) => {
              const options = focusAreaOptions[category as keyof typeof focusAreaOptions];
              const categoryFocusAreas = getCategoryFocusAreas(category);
              const isOpen = openAccordions.has(category);

              return (
                <FocusAccordionItem
                  key={category}
                  category={category}
                  selectedCount={categoryFocusAreas.length}
                  totalCount={options.length}
                  isOpen={isOpen}
                  onToggleOpen={() => toggleAccordion(category)}
                >
                  <div className="flex flex-col gap-2">
                    {options.map((option) => {
                      const isAreaSelected = focusAreas.includes(option);

                      return (
                        <button
                          key={option}
                          type="button"
                          onClick={() => handleFocusAreaToggle(option)}
                          className={cx(
                            'flex items-center gap-3 rounded-xl px-3 py-2.5 transition text-left',
                            isAreaSelected
                              ? 'bg-[#304031]/10 text-[#304031]'
                              : 'hover:bg-[#304031]/5 text-[#304031]'
                          )}
                        >
                          <span
                            aria-hidden
                            className={cx(
                              'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-md border transition',
                              isAreaSelected
                                ? 'border-[#304031] bg-[#304031]'
                                : 'border-[#304031]/40'
                            )}
                          >
                            {isAreaSelected && (
                              <svg
                                className="h-3 w-3 text-white"
                                viewBox="0 0 12 12"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="2,6 5,9 10,3" />
                              </svg>
                            )}
                          </span>
                          <span className={cx('text-sm', isAreaSelected && 'font-medium')}>{option}</span>
                        </button>
                      );
                    })}
                  </div>
                </FocusAccordionItem>
              );
            })}
          </div>
        </div>
      );
    }

    // Special handling for excluded areas question
    if (step.id === 'excludedAreas') {
      const getExcludedCategoryAreas = (category: string) => {
        const options = focusAreaOptions[category as keyof typeof focusAreaOptions];
        return excludedAreas.filter((area) => options.includes(area));
      };

      const toggleExcludedAccordion = (category: string) => {
        setExcludedOpenAccordions((prev) => {
          const next = new Set(prev);
          if (next.has(category)) {
            next.delete(category);
          } else {
            next.add(category);
          }
          return next;
        });
      };

      const handleExcludedAreaToggle = (area: string) => {
        if (focusAreas.includes(area)) return;
        setExcludedAreas((prev) =>
          prev.includes(area) ? prev.filter((a) => a !== area) : [...prev, area]
        );
      };

      return (
        <div>
          <div className="mb-8">
            <h2 className="text-[42px] leading-[44px] font-medium mb-3 text-[#304031] font-recoleta">Are there any conditions you specifically DO NOT work with?</h2>
            <div className="space-y-3">
              <p className="text-[#304031]/70">
                Select any areas you prefer not to work with. This helps us avoid matching you with seekers whose needs fall outside your scope.
              </p>
            </div>
          </div>

          {/* Summary bar */}
          <div className="mb-6 rounded-2xl bg-[#214d3c] p-6">
            <p className="text-xl font-bold text-[#fffcef] leading-[1.6] mb-2">
              Excluded areas: {excludedAreas.length}
            </p>
            {excludedAreas.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {excludedAreas.map((area) => (
                  <span
                    key={area}
                    className="inline-flex items-center h-8 rounded-full border border-[#8d8d8d] px-3 text-sm text-[#fef6d6]"
                  >
                    {area}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#fef6d6]/70">No areas excluded yet</p>
            )}
          </div>

          {/* Accordion list */}
          <div className="flex flex-col gap-3">
            {focusAreaCategories.map((category) => {
              const options = focusAreaOptions[category as keyof typeof focusAreaOptions];
              const categoryExcludedAreas = getExcludedCategoryAreas(category);
              const isOpen = excludedOpenAccordions.has(category);

              return (
                <FocusAccordionItem
                  key={category}
                  category={category}
                  selectedCount={categoryExcludedAreas.length}
                  totalCount={options.length}
                  isOpen={isOpen}
                  onToggleOpen={() => toggleExcludedAccordion(category)}
                >
                  <div className="flex flex-col gap-2">
                    {options.map((option) => {
                      const isExcluded = excludedAreas.includes(option);
                      const isFocusArea = focusAreas.includes(option);

                      return (
                        <button
                          key={option}
                          type="button"
                          onClick={() => handleExcludedAreaToggle(option)}
                          disabled={isFocusArea}
                          className={cx(
                            'flex items-center gap-3 rounded-xl px-3 py-2.5 transition text-left',
                            isFocusArea
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : isExcluded
                                ? 'bg-red-100 text-red-900'
                                : 'hover:bg-red-50 text-[#304031]'
                          )}
                        >
                          <span
                            aria-hidden
                            className={cx(
                              'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-md border transition',
                              isExcluded
                                ? 'border-red-600 bg-red-600'
                                : isFocusArea
                                  ? 'border-gray-300 bg-gray-200'
                                  : 'border-[#304031]/40'
                            )}
                          >
                            {isExcluded && (
                              <svg
                                className="h-3 w-3 text-white"
                                viewBox="0 0 12 12"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <line x1="3" y1="3" x2="9" y2="9" />
                                <line x1="9" y1="3" x2="3" y2="9" />
                              </svg>
                            )}
                            {isFocusArea && (
                              <svg
                                className="h-3 w-3 text-gray-400"
                                viewBox="0 0 12 12"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="2,6 5,9 10,3" />
                              </svg>
                            )}
                          </span>
                          <span className={cx('text-sm', isExcluded && 'font-medium')}>
                            {option}
                            {isFocusArea && <span className="ml-2 text-xs text-gray-400">(selected as focus area)</span>}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </FocusAccordionItem>
              );
            })}
          </div>
        </div>
      );
    }

    if (step.type === 'radio') {
      return (
        <fieldset className="flex flex-col gap-3 w-full">
          <legend className="text-[42px] leading-[44px] font-medium mb-2 font-recoleta">
            {step.question} {step.required && '*'}
          </legend>
          {(step as any).subtext && (
            <p className="text-base text-[#304031]/70 mb-4 whitespace-pre-line">{(step as any).subtext}</p>
          )}
          {step.options.map((opt, index) => {
            const selected = step.value === opt;
            const optionId = `${step.id}-${index}`;
            const { main, sub } = splitOptionText(opt);
            return (
              <label
                key={opt}
                htmlFor={optionId}
                className={`flex items-start gap-3 rounded-xl border border-transparent px-4 py-3 text-base transition hover:bg-[#304031]/5 cursor-pointer ${
                  selected ? 'font-semibold text-[#2C2731]' : 'text-[#2C2731]'
                }`}
              >
                <input
                  id={optionId}
                  type="radio"
                  name={step.id}
                  checked={selected}
                  onChange={() => step.setValue(opt)}
                  className="sr-only"
                />
                <span
                  aria-hidden
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition ${
                    selected ? 'border-[#304031]' : 'border-[#304031]/40'
                  }`}
                >
                  <span
                    className={`h-2.5 w-2.5 rounded-full transition ${
                      selected ? 'bg-[#304031]' : 'bg-transparent'
                    }`}
                  />
                </span>
                <span className="flex flex-col">
                  <span className="text-base text-[#2C2731]">{main}</span>
                  {sub && <span className="text-sm italic text-[#214D3C]">{sub}</span>}
                </span>
              </label>
            );
          })}
        </fieldset>
      );
    }
    
    if (step.type === 'textarea') {
      return (
        <div className="w-full">
          <label className="block text-[42px] leading-[44px] font-medium mb-3 font-recoleta">
            {step.question} {step.required && '*'}
          </label>
          {step.id === 'medicationQualifications' && (
            <div className="mb-6 space-y-3">
              <p className="text-[#304031]/70">
                To ensure safe matching, please share any of the following that apply:
              </p>
              <ul className="list-disc list-inside text-[#304031]/70 space-y-1 ml-2">
                <li>Formal training related to contraindications or medication interactions</li>
                <li>Clinical background or supervision</li>
                <li>Use of licensed medical professionals for screening</li>
                <li>Specific safety protocols or tapering guidelines (if applicable)</li>
                <li>Experience with medication-interaction harm reduction</li>
              </ul>
            </div>
          )}
          <textarea
            rows={6}
            value={step.value}
            onChange={(e) => step.setValue(e.target.value)}
            placeholder={step.placeholder}
            className="w-full rounded-lg border border-[#a0978a] bg-[#fffcef] px-3 py-2 text-sm text-[#304031] placeholder:text-[#304031]/40 focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]"
          />
        </div>
      );
    }

    if (step.type === 'checkbox') {
      const checkboxStep = step as CheckboxStep;
      return (
        <fieldset className="flex flex-col gap-3 w-full">
          <legend className="text-[42px] leading-[44px] font-medium mb-2 font-recoleta">
            {step.question} {step.required && '*'}
          </legend>
          {(step as any).subtext && (
            <p className="text-base text-[#304031]/70 mb-4 whitespace-pre-line">{(step as any).subtext}</p>
          )}

          {checkboxStep.options.map((option, index) => {
            const selected = checkboxStep.value.includes(option);
            const optionId = `${step.id}-${index}`;
            const { main, sub } = splitOptionText(option);
            return (
              <label
                key={option}
                htmlFor={optionId}
                className={`flex items-start gap-3 rounded-xl border border-transparent px-4 py-3 text-base transition hover:bg-[#304031]/5 cursor-pointer ${
                  selected ? 'font-semibold text-[#2C2731]' : 'text-[#2C2731]'
                }`}
              >
                <input
                  id={optionId}
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggleArrayValue(checkboxStep.value, option, checkboxStep.setValue)}
                  className="sr-only"
                />
                <span
                  aria-hidden
                  className={`mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-md border border-[#304031]/40 transition ${
                    selected ? 'border-[#304031] bg-[#304031]' : ''
                  }`}
                >
                  {selected && (
                    <svg
                      className="h-3 w-3 text-white"
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="2,6 5,9 10,3" />
                    </svg>
                  )}
                </span>
                <span className="flex flex-col">
                  <span className="text-base text-[#2C2731]">{main}</span>
                  {sub && <span className="text-sm italic text-[#214D3C]">{sub}</span>}
                </span>
              </label>
            );
          })}
        </fieldset>
      );
    }
  };

  return (
    <div className="min-h-screen bg-[#fffcef] px-4">
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#304031]"></div>
          <span className="ml-3 text-[#304031]/70">Loading your information...</span>
        </div>
      ) : (
        <div className="mx-auto w-[90vw] py-8 text-[#304031]">
          {/* Question content */}
          <div className="mb-8">
            {renderQuestion()}
          </div>

          {/* Navigation */}
          <div className="flex gap-4 items-center">
            <button
              onClick={handlePrevious}
              disabled={saving}
              className="inline-flex items-center gap-2 border border-[#304031] px-5 py-2 rounded-full hover:bg-[#304031]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Back
            </button>

            <button
              onClick={handleNext}
              disabled={!canProceedToNext() || saving}
              className={`px-6 py-2 rounded-full transition-colors flex items-center gap-2 ${
                canProceedToNext() && !saving
                  ? 'bg-[#304031] text-[#fef6d6] hover:bg-[#304031]/90'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {saving && (
                <div className="animate-spin h-4 w-4 border-2 border-[#fef6d6] border-t-transparent rounded-full"></div>
              )}
              {isLastStep ? (saving ? 'Saving...' : 'Continue') : 'Continue'} →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
